import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { Drawer } from 'antd';
import { ChatProvider, useChatContext } from '../bot-core/store/ChatContext';
import { BotExtProvider, useBotContext } from './store/BotContext';
import { useBotSession } from './hooks/useBotSession';
import { useChatSocket } from '../bot-core/hooks/useChatSocket';
import { useWhatsappHandoff } from './hooks/useWhatsappHandoff';
import type { WhatsappAvailability } from './hooks/useBotAgentAvailability';
import { FaWhatsapp } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import ChatButton from '../bot-core/ChatButton';
import ChatHeader from '../bot-core/ChatHeader';
import ChatMessages from '../bot-core/ChatMessages';
import ChatInput from '../bot-core/ChatInput';
import ItinerarySummaryCard from './ItinerarySummaryCard';
import CartPreviewCard from './CartPreviewCard';
import CheckoutCtaButton from './CheckoutCtaButton';
import CheckoutPanel from './CheckoutPanel';
import SearchResultList from './search-results/SearchResultList';
import RoomOptionsList from './search-results/RoomOptionsList';
import { UIMessage } from '../bot-core/types/chat.types';
import { IoBagHandleOutline } from 'react-icons/io5';
import { Tabs } from 'antd';
import {
  useBotCheckoutHandler,
  extractBotSessionId,
} from './hooks/useBotCheckoutHandler';
import '../bot-core/chat.less';
import './bot.less';
import './drawer-bot.less';

const TOOL_TO_PRODUCT: Record<string, 'flight' | 'hotel' | 'package' | 'activity' | 'event'> = {
  search_flights: 'flight',
  search_hotels: 'hotel',
  search_packages: 'package',
  search_attractions: 'activity',
  search_events: 'event',
};

const MIN_WIDTH = 300;
const MAX_WIDTH_RATIO = 0.7;
const DEFAULT_WIDTH_RATIO = 0.4;

interface DrawerBotWidgetInnerProps {
  whatsapp?: WhatsappAvailability | null;
}

const DrawerBotWidgetInner: React.FC<DrawerBotWidgetInnerProps> = ({
  whatsapp,
}) => {
  const { state: chatState, dispatch: chatDispatch } = useChatContext();
  const { botState, botDispatch } = useBotContext();
  const {
    initSession,
    refreshSelections,
    fetchCheckout,
    tabs,
    activeTabId,
    createTab,
    switchTab,
    closeTab,
  } = useBotSession();

  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const tenantId = Number(process.env.REACT_APP_TENANT_ID) || undefined;

  const [drawerWidth, setDrawerWidth] = useState(() =>
    Math.round(window.innerWidth * DEFAULT_WIDTH_RATIO)
  );
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onDone = useCallback(async (meta: any) => {
    if (!botState.sessionId) return;
    await refreshSelections(botState.sessionId);
    // See BotWidget.onDone for the rationale on tracking both tools.
    if (
      meta?.lastTool === 'initiate_checkout' ||
      meta?.lastTool === 'generate_checkout_url'
    ) {
      await fetchCheckout(botState.sessionId);
    }
  }, [botState.sessionId, refreshSelections, fetchCheckout]);

  const onHandedOff = useCallback(
    (handedSessionId: string) => {
      if (handedSessionId === botState.sessionId) {
        chatDispatch({ type: 'SET_OPEN', isOpen: false });
      }
    },
    [botState.sessionId, chatDispatch],
  );

  const { connect, disconnect, emit } = useChatSocket({
    namespace: '/bot-agent',
    onDone,
    onHandedOff,
  });

  const { handoff: triggerWhatsappHandoff, busy: handoffBusy } =
    useWhatsappHandoff();

  useEffect(() => { initSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chatState.isOpen && botState.sessionId) connect();
    if (!chatState.isOpen) disconnect();
  }, [chatState.isOpen, botState.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = () => chatDispatch({ type: 'SET_OPEN', isOpen: !chatState.isOpen });
  const handleClose = () => chatDispatch({ type: 'SET_OPEN', isOpen: false });

  const handleSend = (
    text: string,
    opts?: { selection?: { book_hash?: string } },
  ) => {
    if (!botState.sessionId) return;
    emit('send_message', {
      session_id: botState.sessionId,
      text,
      ...(tenantId ? { tenant_id: tenantId } : {}),
      ...(opts?.selection ? { selection: opts.selection } : {}),
    });
  };

  const isBlocked = botState.sessionStatus === 'blocked';
  const hasActivity = chatState.messages.length > 0 || botState.selections.length > 0;

  // See BotWidget.onLinkClick — same bot-checkout interception.
  const handleBotCheckout = useBotCheckoutHandler();
  const onLinkClick = useCallback(
    (url: string, _e: React.MouseEvent): boolean => {
      const sid = extractBotSessionId(url);
      if (!sid) return false;
      handleBotCheckout(sid);
      return true;
    },
    [handleBotCheckout],
  );

  const handleRemoveItem = (displayName: string) => {
    handleSend(`Remove ${displayName}`);
  };

  // ── Drag-to-resize ───────────────────────────────────────────────────────────
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = drawerWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      // Drawer is on the right edge; dragging left → larger width
      const delta = startX.current - ev.clientX;
      const raw = startWidth.current + delta;
      const maxWidth = Math.round(window.innerWidth * MAX_WIDTH_RATIO);
      setDrawerWidth(Math.min(Math.max(raw, MIN_WIDTH), maxWidth));
    };

    const onMouseUp = () => {
      isResizing.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [drawerWidth]);

  // ── Message extras ───────────────────────────────────────────────────────────
  const lastMsgId = chatState.messages[chatState.messages.length - 1]?.id;
  const renderMessageExtra = (msg: UIMessage) => {
    const extras: React.ReactNode[] = [];
    const showRoomOptions =
      !!msg.productDetails?.raw?.pricing &&
      msg.id === lastMsgId &&
      !chatState.isStreaming;
    if (msg.searchResults && msg.id === lastMsgId && !chatState.isStreaming) {
      const productType = TOOL_TO_PRODUCT[msg.searchResults.tool] ?? 'hotel';
      extras.push(
        <SearchResultList
          key="search-results"
          productType={productType}
          results={msg.searchResults.results}
          items={msg.searchResults.items}
          onSelect={(index, displayName) => {
            handleSend(`I'll take option ${index + 1} — ${displayName}`);
          }}
        />,
      );
    }
    if (showRoomOptions) {
      extras.push(
        <RoomOptionsList
          key="room-options"
          raw={msg.productDetails!.raw}
          onSelect={(bookHash, friendlyText) => {
            handleSend(friendlyText, { selection: { book_hash: bookHash } });
          }}
        />,
      );
    }
    if (msg.cartPreview && !chatState.isStreaming) {
      extras.push(<CartPreviewCard key="cart-preview" cartPreview={msg.cartPreview} />);
    }
    // Structured checkout CTA from the generate_checkout_url sideband.
    if (msg.checkoutReady && !chatState.isStreaming) {
      extras.push(
        <CheckoutCtaButton
          key="checkout-cta"
          sessionId={msg.checkoutReady.session_id}
          expiresAt={msg.checkoutReady.expires_at}
          onCheckout={handleBotCheckout}
          label={t?.checkout?.completeBooking}
        />,
      );
    }
    return extras.length ? <>{extras}</> : null;
  };

  // See BotWidget — same "Preview cart" toggle.
  const hasCheckoutPayload = !!botState.checkoutPayload;
  const showWhatsappHandoff =
    !!whatsapp?.enabled &&
    !!whatsapp?.business_number &&
    !!botState.sessionId;

  const headerActions = (
    <>
      {showWhatsappHandoff && (
        <button
          className="bot-icon-btn"
          // TODO(i18n): translation key bot.handoff.continueOnWhatsapp
          title="Continue on WhatsApp"
          disabled={handoffBusy}
          onClick={() => {
            if (botState.sessionId) triggerWhatsappHandoff(botState.sessionId);
          }}
        >
          <FaWhatsapp size={20} />
        </button>
      )}
      {hasCheckoutPayload && (
        <button
          className={`bot-icon-btn${botState.checkoutReady ? ' bot-icon-btn--active' : ''}`}
          title={t?.previewCart ?? 'Preview cart'}
          onClick={() =>
            botDispatch({
              type: botState.checkoutReady ? 'HIDE_CHECKOUT' : 'SHOW_CHECKOUT',
            })
          }
        >
          <IoBagHandleOutline size={20} />
        </button>
      )}
    </>
  );

  // Cache active session's first user message as the tab label.
  const firstUserText = useMemo(() => {
    const m = chatState.messages.find((m) => m.role === 'user');
    return m?.text;
  }, [chatState.messages]);
  useEffect(() => {
    if (activeTabId && firstUserText) {
      botDispatch({
        type: 'SET_TAB_TITLE',
        sessionId: activeTabId,
        title: firstUserText.slice(0, 40),
      });
    }
  }, [activeTabId, firstUserText, botDispatch]);

  // Browser-style tabs (see BotWidget for design notes).
  const newChatLabel = t?.newChat ?? 'New chat';
  const tabItems = tabs.map((tab) => ({
    key: tab.id,
    label:
      (botState.tabTitles[tab.id] && botState.tabTitles[tab.id].trim()) ||
      (tab.title && tab.title.trim()) ||
      newChatLabel,
    children: null,
    closable: true,
  }));
  const tabsStrip = tabs.length > 0 ? (
    <div className="bot-tabs-strip">
      <Tabs
        type="editable-card"
        activeKey={activeTabId ?? undefined}
        items={tabItems}
        // hideAdd={false} // TODO: Add back in when we have a way to create new chats
        hideAdd={true} // TODO: Add back in when we have a way to create new chats
        onChange={(key) => switchTab(key)}
        onEdit={(targetKey, action) => {
          if (action === 'add') {
            createTab();
          } else if (action === 'remove' && typeof targetKey === 'string') {
            closeTab(targetKey);
          }
        }}
      />
    </div>
  ) : null;

  return (
    <>
      {/* FAB — shown only when drawer is closed */}
      {!chatState.isOpen && (
        <ChatButton isOpen={false} onClick={handleToggle} hasActivity={hasActivity} />
      )}

      {/* Drag-to-resize handle — fixed, lines up with drawer's left edge */}
      {chatState.isOpen && (
        <div
          className="bot-drawer__resize-handle"
          style={{ right: drawerWidth }}
          onMouseDown={startResize}
        />
      )}

      <Drawer
        className="bot-drawer"
        placement="right"
        visible={chatState.isOpen}
        width={drawerWidth}
        mask={false}
        closable={false}
        title={null}
        zIndex={9998}
        bodyStyle={{
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          height: '100%',
        }}
        drawerStyle={{ display: 'flex', flexDirection: 'column' }}
      >
        <ChatHeader
          title={t?.title ?? 'Sky360 Travel Assistant'}
          avatarEmoji="✈️"
          extraActions={headerActions}
          onClose={handleClose}
        />
        {tabsStrip}
        <ChatMessages
          avatarEmoji="✈️"
          isBlocked={isBlocked}
          emptyStateContent={
            <div className="bot-empty-state">
              <div className="bot-empty-state__icon">✈️</div>
              <div className="bot-empty-state__title">
                {t?.emptyState?.title ?? 'How can I help plan your trip?'}
              </div>
              <div className="bot-empty-state__sub">
                {t?.emptyState?.subtitle ?? 'Ask me about flights, hotels, packages, or activities.'}
              </div>
            </div>
          }
          blockedMessage={t?.blocked ?? 'This chat has been disabled.'}
          extraBottom={
            botState.selections.length > 0
              ? <ItinerarySummaryCard onRemove={handleRemoveItem} />
              : null
          }
          renderMessageExtra={renderMessageExtra}
          onLinkClick={onLinkClick}
        />
        <ChatInput
          onSend={handleSend}
          isBlocked={isBlocked}
          placeholder={t?.input?.placeholder ?? 'Ask me anything about your trip…'}
        />
        {botState.checkoutReady && <CheckoutPanel />}
      </Drawer>
    </>
  );
};

interface DrawerBotWidgetProps {
  whatsapp?: WhatsappAvailability | null;
}

const DrawerBotWidget: React.FC<DrawerBotWidgetProps> = ({ whatsapp }) => (
  <ChatProvider>
    <BotExtProvider>
      <DrawerBotWidgetInner whatsapp={whatsapp} />
    </BotExtProvider>
  </ChatProvider>
);

export default DrawerBotWidget;
